<template>
  <div class="filter-drawer" :class="{ 'open': isOpen }">
    <div class="filter-drawer-header">
      <h5>Advanced Filters</h5>
      <button type="button" class="btn-close" @click="$emit('close')">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <div class="filter-drawer-body">
      <form @submit.prevent="applyFilters">
        <div class="row">
          <!-- Add Receipt Number filter -->
          <div class="col-md-12 mb-3">
            <label class="form-label">Receipt Number</label>
            <input
              type="text"
              class="form-control"
              v-model="filters.number"
              placeholder="Enter receipt number"
            >
          </div>

          <div class="col-md-12 mb-3">
            <label class="form-label">Payment Method</label>
            <multiselect
              v-model="selectedPaymentMethod"
              :options="paymentMethodOptions"
              :custom-label="paymentMethodLabel"
              placeholder="Select Payment Method"
              track-by="id"
              :loading="loadingPaymentMethods"
              :searchable="true"
              :allow-empty="true"
              @search-change="searchPaymentMethods"
            >
              <template slot="noResult">
                No payment methods found.
              </template>
            </multiselect>
          </div>

          <div class="col-md-12 mb-3">
            <label class="form-label">Currency</label>
            <select class="form-control" v-model="filters.currency">
              <option value="">All Currencies</option>
              <option v-for="currency in currencies" :key="currency" :value="currency">
                {{ currency }}
              </option>
            </select>
          </div>

          <div class="col-md-12 mb-3" v-if="$i18n.locale === 'levies'">
            <label class="form-label">Project</label>
            <select class="form-control" v-model="filters.projectId">
              <option value="">All Projects</option>
              <option value="__NULL__">No Project</option>
              <option v-for="project in projects" :key="project.id" :value="project.id">
                {{ project.name }}
              </option>
            </select>
          </div>

          <div class="col-md-6 mb-3">
            <label class="form-label">Start Date</label>
            <input
              type="date"
              class="form-control"
              v-model="filters.startDate"
            >
          </div>

          <div class="col-md-6 mb-3">
            <label class="form-label">End Date</label>
            <input
              type="date"
              class="form-control"
              v-model="filters.endDate"
            >
          </div>

          <div class="col-md-6 mb-3">
            <label class="form-label">Min Amount</label>
            <input
              type="number"
              class="form-control"
              v-model="filters.minAmount"
              step="0.01"
              placeholder="0.00"
            >
          </div>

          <div class="col-md-6 mb-3">
            <label class="form-label">Max Amount</label>
            <input
              type="number"
              class="form-control"
              v-model="filters.maxAmount"
              step="0.01"
              placeholder="0.00"
            >
          </div>
        </div>

        <div class="filter-drawer-footer">
          <button type="button" class="btn btn-secondary me-2" @click="clearFilters">
            Clear Filters
          </button>
          <button type="submit" class="btn btn-primary">
            Apply Filters
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import paymentMethodService from '../../service/payment.method.service';
import {handleError} from '../../utils/notification.utils';
import {useCurrency} from '../../composables/useCurrency';

export default {
  name: 'ReceiptFilterDrawer',
  components: {
    Multiselect
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    initialFilters: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      filters: {
        number: '',
        paymentMethodId: '',
        projectId: '',
        currency: '',
        startDate: '',
        endDate: '',
        minAmount: '',
        maxAmount: ''
      },
      selectedPaymentMethod: null,
      paymentMethods: [],
      projects: [],
      currencies: [],
      loadingPaymentMethods: false,
      loadingProjects: false,
      loadingCurrencies: false,
      merchantId: localStorage.getItem('mid'),
      searchTimeout: null,
      hasLoadedData: false
    };
  },
  computed: {
    paymentMethodOptions() {
      // Add "All Payment Methods" option at the beginning
      const allPaymentMethodsOption = {
        id: '',
        name: 'All Payment Methods',
        description: 'All Payment Methods'
      };
      return [allPaymentMethodsOption, ...this.paymentMethods];
    }
  },
  watch: {
    initialFilters: {
      handler(newFilters) {
        if (newFilters) {
          this.filters = {...this.filters, ...newFilters};
          if (newFilters.paymentMethodId) {
            if (newFilters.paymentMethodId === '') {
              this.selectedPaymentMethod = this.paymentMethodOptions[0];
            } else if (this.paymentMethods.length > 0) {
              this.selectedPaymentMethod = this.paymentMethods.find(method => method.id === newFilters.paymentMethodId) || null;
            }
          }
        }
      },
      immediate: true
    },
    selectedPaymentMethod: {
      handler(newVal) {
        this.filters.paymentMethodId = newVal ? newVal.id : '';
      }
    },
    isOpen: {
      async handler(newValue) {
        if (newValue && !this.hasLoadedData) {
          // Only load data once when drawer is first opened
          await Promise.all([
            this.loadPaymentMethods(),
            this.loadProjects(),
            this.loadCurrencies()
          ]);
          this.hasLoadedData = true;
        }
      },
      immediate: false
    }
  },
  methods: {
    paymentMethodLabel(paymentMethod) {
      return `${paymentMethod.name || paymentMethod.description}`;
    },

    async searchPaymentMethods(query) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = setTimeout(async () => {
        if (query.length >= 2) {
          this.loadingPaymentMethods = true;
          try {
            const params = {
              page: 1,
              size: 20,
              name: query,
              op: 'like'
            };

            const response = await paymentMethodService.find(this.merchantId, params);
            if (response.data && (response.data.success || response.data.code === 'SUCCESS')) {
              this.paymentMethods = response.data.data || [];
            }
          } catch (error) {
            handleError(error);
          } finally {
            this.loadingPaymentMethods = false;
          }
        }
      }, 300);
    },

    async loadPaymentMethods() {
      this.loadingPaymentMethods = true;
      try {
        const params = {
          page: 1,
          size: 10
        };

        const response = await paymentMethodService.find(this.merchantId, params);
        if (response.data && (response.data.success || response.data.code === 'SUCCESS')) {
          this.paymentMethods = response.data.data || [];

          // Set default to "All Payment Methods" if no initial filter is set
          if (!this.selectedPaymentMethod && this.paymentMethodOptions.length > 0) {
            this.selectedPaymentMethod = this.paymentMethodOptions[0];
          }
        }
      } catch (error) {
        handleError(error);
      } finally {
        this.loadingPaymentMethods = false;
      }
    },

    async loadCurrencies() {
      this.loadingCurrencies = true;
      try {
        // Use the currency composable to get currencies
        const currencyComposable = useCurrency();
        const {currencies} = await currencyComposable.initializeCurrencies();

        this.currencies = currencies;
      } catch (error) {
        console.error('Error loading currencies:', error);
        handleError(error);

        // Fallback to empty list
        this.currencies = ['USD', 'ZWL', 'ZAR'];
      } finally {
        this.loadingCurrencies = false;
      }
    },

    async loadProjects() {
      // Only load projects if locale is 'levies'
      if (this.$i18n.locale !== 'levies') {
        return;
      }

      this.loadingProjects = true;
      try {
        const params = {
          page: 1,
          size: 100,
          sortBy: 'name',
          sortOrder: 'ASC'
        };

        const response = await projectService.find(params);
        if (response.data && (response.data.success || response.data.code === 'SUCCESS')) {
          this.projects = response.data.data || [];
        }
      } catch (error) {
        console.error('Error loading projects:', error);
        handleError(error);
      } finally {
        this.loadingProjects = false;
      }
    },

    applyFilters() {
      // Create a copy of filters to modify
      const payload = {...this.filters};

      // Handle the special __NULL__ case for projectId
      if (payload.projectId === '__NULL__') {
        payload.projectId = null;
        payload.projectIdIsNull = true;
      }

      this.$emit('filters-updated', payload);
    },

    clearFilters() {
      this.filters = {
        number: '',
        paymentMethodId: '',
        projectId: '',
        currency: '',
        startDate: '',
        endDate: '',
        minAmount: '',
        maxAmount: ''
      };
      // Reset to "All Payment Methods" option
      this.selectedPaymentMethod = this.paymentMethodOptions.length > 0 ? this.paymentMethodOptions[0] : null;
      this.$emit('filters-updated', this.filters);
    }
  },

  async mounted() {
    // Remove automatic data loading on mount
    // Data will be loaded when drawer is opened (see isOpen watcher)
    this.hasLoadedData = false;
  }
};
</script>

<style scoped>
.filter-drawer {
  position: fixed;
  top: 0;
  right: -400px;
  width: 350px;
  height: 100vh;
  background: white;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  z-index: 1050;
  transition: right 0.3s ease;
  overflow-y: auto;
}

.filter-drawer.open {
  right: 0;
}

.filter-drawer-header {
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
}

.filter-drawer-header h5 {
  margin: 0;
  color: #495057;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: #6c757d;
}

.btn-close:hover {
  color: #000;
}

.filter-drawer-body {
  padding: 1rem;
}

.filter-drawer-footer {
  padding: 1rem;
  border-top: 1px solid #dee2e6;
  background-color: #f8f9fa;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.form-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
}

.form-control {
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  padding: 0.375rem 0.75rem;
}

.form-control:focus {
  border-color: #86b7fe;
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

.me-2 {
  margin-right: 0.5rem;
}
</style>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>



// WEBPACK FOOTER //
// src/components/receipts/ReceiptFilterDrawer.vue